import { ChangeEventHandler } from 'react';
import ErrorIcon from '@mui/icons-material/Error';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import LinkIcon from '@mui/icons-material/Link';
import { LinearProgress } from '@mui/material';
import Modal from '@/trio-ui/Modal';
import { forceHttpsProtocol } from '@/utils/url';
import * as Styles from './InterviewRecordingUploader.styles';
import DeleteButton from '../DeleteButton';
import DeleteConfirmationModal from '../DeleteConfirmationModal/DeleteConfirmationModal.component';

export type InterviewRecordingUploaderComponentProps = {
  name: string;
  className?: string;
  recordingUrl?: string;
  onFileChange?: ChangeEventHandler<HTMLInputElement>;
  isUploading?: boolean;
  isDeleting?: boolean;
  uploadProgress?: number;
  uploadError?: string | null;
  showErrorModal?: boolean;
  onCloseErrorModal?: () => void;
  showDeleteConfirmation?: boolean;
  setShowDeleteConfirmation?: (value: boolean) => void;
  onDeleteConfirm: () => void;
  label?: string;
};

const FileInput = ({
  name,
  recordingUrl,
  className,
  onFileChange,
  isUploading = false,
  isDeleting = false,
  uploadProgress = 0,
  uploadError = null,
  showErrorModal = false,
  onCloseErrorModal,
  showDeleteConfirmation = false,
  setShowDeleteConfirmation,
  onDeleteConfirm,
  label = 'Interview Recording',
}: InterviewRecordingUploaderComponentProps): JSX.Element => {
  const ErrorModalBody = (
    <Styles.ErrorMessage>
      {uploadError || 'An error occurred while uploading the recording. Please try again.'}
    </Styles.ErrorMessage>
  );

  return (
    <Styles.Box className={className}>
      <Styles.Title>{label}</Styles.Title>
      {recordingUrl ? (
        <>
          <Styles.UploadedBox>
            <Styles.LabelText>Link</Styles.LabelText>
            <Styles.Link href={recordingUrl} target="_blank" rel="noreferrer">
              <LinkIcon />
              {recordingUrl}
            </Styles.Link>
            <Styles.DeleteButtonContainer>
              <DeleteButton onClick={() => setShowDeleteConfirmation?.(true)} />
            </Styles.DeleteButtonContainer>
          </Styles.UploadedBox>
          <video src={forceHttpsProtocol(recordingUrl)} controls width="100%" />
        </>
      ) : (
        <>
          <Styles.Label htmlFor={name} aria-disabled={isUploading}>
            <Styles.Input
              type="file"
              name={name}
              id={name}
              accept="video/*"
              onChange={onFileChange}
              disabled={isUploading}
            />
            <FileUploadIcon />
            <Styles.LabelText>Upload</Styles.LabelText>
          </Styles.Label>
          {isUploading && (
            <Styles.UploadProgressContainer>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Styles.ProgressText>{uploadProgress}%</Styles.ProgressText>
            </Styles.UploadProgressContainer>
          )}
        </>
      )}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation?.(false)}
        onConfirmHandler={onDeleteConfirm}
        isButtonDisabled={isDeleting}
      />
      <Modal
        modalId="upload-error-modal"
        isCentered
        size="sm"
        headerIcon={ErrorIcon}
        modalTitle="Upload Error"
        bodyRender={ErrorModalBody}
        onClose={onCloseErrorModal || (() => {})}
        isOpen={showErrorModal}
        hideCancelButton
        onConfirmLabel="Close"
        onConfirmClick={onCloseErrorModal || (() => {})}
      />
    </Styles.Box>
  );
};

export default FileInput;
